import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { AuthLegalLinks } from "@/components/layout/AuthLegalLinks";
import { friendlyError } from "@/lib/errors";
import {
  clearPersistedInvitationToken,
  invitationAcceptanceMode,
  invitationRegisterPath,
  invitationTokenFromSearch,
  persistInvitationToken,
  readPersistedInvitationToken,
} from "@/lib/access-invitation";

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inviteFromUrl = invitationTokenFromSearch(searchParams.get("invite"));

  useEffect(() => {
    persistInvitationToken(inviteFromUrl);
  }, [inviteFromUrl]);

  useEffect(() => {
    const token = persistInvitationToken(inviteFromUrl) ?? readPersistedInvitationToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("lookup_invitation", { p_token: token });
      if (cancelled) return;
      const row = data as { ok?: boolean; email?: string; account_status?: string; email_confirmed?: boolean; account_exists?: boolean } | null;
      if (row?.ok && row.email) setEmail(row.email);
      if (row?.ok && invitationAcceptanceMode(row) === "confirm_email" && token) {
        navigate(invitationRegisterPath(token), { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteFromUrl, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      const unconfirmed = /not confirmed|email not confirmed/i.test(signInError.message);
      const token = persistInvitationToken(inviteFromUrl) ?? readPersistedInvitationToken();
      if (unconfirmed && token) {
        persistInvitationToken(token);
        navigate(invitationRegisterPath(token), { replace: true });
        return;
      }
      setError(signInError.message);
      setLoading(false);
      return;
    }
    const u = data.user;
    if (u) {
      const meta = (u.user_metadata ?? {}) as { full_name?: string; org_name?: string };
      const inviteToken = persistInvitationToken(inviteFromUrl) ?? readPersistedInvitationToken();
      const { error: initError } = await supabase.rpc("initialize_account", {
        p_email: u.email ?? email,
        p_full_name: meta.full_name ?? null,
        p_org_name: meta.org_name ?? null,
        p_invite_token: inviteToken,
      });
      if (initError) {
        setError(friendlyError(initError.message));
        setLoading(false);
        return;
      }
      clearPersistedInvitationToken();
      await refresh();
    }
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8">
        <div className="flex items-center justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center mr-3">
            <i className="ri-stack-line text-white" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Tradivo</h1>
        </div>
        <h2 className="text-lg font-semibold text-slate-800 text-center mb-1">Welcome back</h2>
        <p className="text-sm text-slate-500 text-center mb-6">
          {inviteFromUrl ? "Sign in to accept your invitation" : "Sign in to your organization"}
        </p>

        {error && (
          <div role="alert" className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              id="login-email"
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              id="login-password"
              type="password"
              name="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              placeholder="••••••••"
            />
            <div className="flex justify-end mt-1">
              <Link to="/forgot-password" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">
                Forgot password?
              </Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-medium rounded-lg text-sm transition-colors whitespace-nowrap"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="text-sm text-slate-500 text-center mt-4">
          Tradivo is invitation-only. If you were invited, use the link in your email.
        </p>
        <AuthLegalLinks />
      </div>
    </div>
  );
}
